import { Injectable, inject } from '@angular/core';
import {Logger} from '../logging/default-log.service';
import {Product} from '../classes/product';
import {ProductDetail} from '../classes/product.detail';
import {ShoppingCartLine} from '../classes/shoppingcartline';

@Injectable()
export class TagManagerService {
	private logger: Logger;
	private _dataLayer = (<any>window).dataLayer;

	constructor() {
		const logger = inject(Logger);

		this.logger = logger;
	}

	private checkIfTagManagerIsAvailable() {
		if (this._dataLayer === undefined) {
			this.logger.info(`Analytics library could not be found`);
			return false;
		}

		return true;
	}

	/**
	 * Sends the correct information for a product click to the tag manager.
	 *
	 * @param product
	 */
	public trackProductViewClick(product: Product) {
		if (!this.checkIfTagManagerIsAvailable()) {
			return;
		}

		this._dataLayer.push({
			var: 'productClick',
			event: 'productClick',
			ecommerce: {
				actionField: {
					list: `${product.totalDescription1}`
				},
				click: {
					products: [
						{
							name: `${product.totalDescription1}`,
							id: `${product.priceListNo}${product.priceListLineNo}`,
							price: `${product.unitPrice}`,
							variant: `${product.type}`
						}
					]
				}
			}
		});
	}

	/**
	 * Tracks a product detail view. This is sadly not done with PageViews since Angular is route based.
	 * We could set the Tag Manager to listen for History changes but that might pollute the data a bit.
	 *
	 * @param product
	 */
	public trackProductDetailView(product: ProductDetail) {
		if (!this.checkIfTagManagerIsAvailable()) {
			return;
		}

		this._dataLayer.push({
			var: 'productView',
			event: 'productView',
			ecommerce: {
				actionField: {
					list: `${product.totalDescription1}`
				},
				detail: {
					products: [
						{
							name: `${product.totalDescription1}`,
							id: `${product.priceListNo}${product.priceListLineNo}`,
							price: `${product.unitPrice}`,
							brand: `${product.grower}`,
							variant: `${product.type}`
						}
					]
				}
			}
		});
	}

	/**
	 * Track the add of a single product to the shoppingcart
	 *
	 * @param product
	 * @param amount
	 * @param price
	 */
	public trackAddToCart(
		product: Product | ProductDetail,
		amount: number,
		price: number
	) {
		if (!this.checkIfTagManagerIsAvailable()) {
			return;
		}

		this._dataLayer.push({
			var: 'addToCart',
			event: 'addToCart',
			ecommerce: {
				add: {
					products: [
						{
							name: `${product.totalDescription1}`,
							id: `${product.priceListNo}${product.priceListLineNo}`,
							price: `${price}`,
							quantity: amount
						}
					]
				}
			}
		});
	}

	/**
	 * Track the removal of a product from the shoppingcart.
	 *
	 * @param line
	 */
	public trackRemoveFromCart(line: ShoppingCartLine) {
		if (!this.checkIfTagManagerIsAvailable()) {
			return;
		}

		this._dataLayer.push({
			var: 'removeFromCart',
			event: 'removeFromCart',
			ecommerce: {
				remove: {
					products: [
						{
							name: `${line.description}`,
							id: `${line.priceListNo}${line.priceListLineNo}`,
							price: `${line.unitPriceExcResale}`
						}
					]
				}
			}
		});
	}
}
